import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Square } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { parseSSE, streamChat } from '@/lib/chatStream';

export const Route = createFileRoute('/chat')({
  component: ChatPage,
});

type ToolCall = { id: string; name: string; status: 'running' | 'done' | 'error' };
interface Msg {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCall[];
  streaming?: boolean;
}

const SUGGESTIONS = [
  'Combien j\'ai dépensé en restaurants ce mois ?',
  'Quelle est ma plus grosse dépense de la semaine ?',
  'Top 5 contreparties du dernier trimestre ?',
  'Mes dépenses de Courses ont-elles baissé vs le mois dernier ?',
];

function ChatPage() {
  const qc = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const updateLast = useCallback((fn: (m: Msg) => Msg) => {
    setMessages((arr) => {
      if (arr.length === 0) return arr;
      const last = arr[arr.length - 1];
      if (!last || last.role !== 'assistant') return arr;
      return [...arr.slice(0, -1), fn(last)];
    });
  }, []);

  const sendMut = useMutation({
    mutationFn: async (msg: string) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setMessages((m) => [
        ...m,
        { role: 'user', content: msg },
        { role: 'assistant', content: '', streaming: true },
      ]);

      const body = await streamChat(msg, conversationId ?? undefined, ac.signal);
      for await (const ev of parseSSE(body)) {
        switch (ev.type) {
          case 'meta':
            if (ev.conversationId) setConversationId(ev.conversationId);
            break;
          case 'text_delta':
            if (ev.text) updateLast((m) => ({ ...m, content: m.content + ev.text }));
            break;
          case 'tool_call_start':
            if (ev.id && ev.name) {
              const id = ev.id;
              const name = ev.name;
              updateLast((m) => ({
                ...m,
                toolCalls: [...(m.toolCalls ?? []), { id, name, status: 'running' }],
              }));
            }
            break;
          case 'tool_running':
            updateLast((m) => ({
              ...m,
              toolCalls: (m.toolCalls ?? []).map((t) =>
                t.id === ev.id ? { ...t, status: 'running' } : t,
              ),
            }));
            break;
          case 'tool_result':
            updateLast((m) => ({
              ...m,
              toolCalls: (m.toolCalls ?? []).map((t) =>
                t.id === ev.id ? { ...t, status: ev.ok ? 'done' : 'error' } : t,
              ),
            }));
            break;
          case 'turn_end':
            if (ev.stopReason === 'tool_use') {
              setMessages((arr) => [...arr, { role: 'assistant', content: '', streaming: true }]);
            }
            break;
          case 'done':
            updateLast((m) => ({ ...m, streaming: false }));
            break;
          case 'error':
            updateLast((m) => ({
              ...m,
              streaming: false,
              content: m.content + `\n\n_Erreur : ${ev.message ?? 'inconnue'}_`,
            }));
            throw new Error(ev.message ?? 'stream error');
        }
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['chat'] });
    },
  });

  const send = (text: string) => {
    if (!text.trim() || sendMut.isPending) return;
    setInput('');
    sendMut.mutate(text);
  };
  const cancel = () => abortRef.current?.abort();

  return (
    <div className="flex h-[calc(100vh-128px)] flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" /> Assistant
        </h1>
        <p className="text-sm text-muted-foreground">Pose toutes tes questions sur tes finances</p>
      </div>

      <Card className="flex-1 overflow-hidden">
        <CardContent className="flex h-full flex-col p-0">
          <div className="flex-1 space-y-4 overflow-y-auto p-6">
            {messages.length === 0 && !sendMut.isPending ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Quelques idées de questions :</p>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {SUGGESTIONS.map((s) => (
                    <Button key={s} variant="outline" className="justify-start" onClick={() => send(s)}>
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  {m.role === 'assistant' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {m.content || (m.streaming ? '…' : '')}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    m.content
                  )}
                  {m.toolCalls && m.toolCalls.length > 0 ? (
                    <div className="mt-2 space-y-0.5 text-xs opacity-70">
                      {m.toolCalls.map((t) => (
                        <div key={t.id}>
                          {t.status === 'running' ? '⏳' : t.status === 'done' ? '✓' : '✗'} {t.name}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex items-center gap-2 border-t border-border p-4"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Demande à ton assistant…"
              disabled={sendMut.isPending}
            />
            {sendMut.isPending ? (
              <Button type="button" variant="destructive" onClick={cancel} aria-label="Stop">
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button type="submit" disabled={!input.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
